import { Chip, Grid, Button } from '@mui/material';
import InputConfig from '../../../../../../Components/InputConfig';
import { useContext, useMemo } from 'react';
import { UnitsContext } from '../../../../../../Contexts/UnitsContext';
import { Modalv2Context } from '../../../../../../Contexts/Modalv2Context';
import AbSplitTestConcludeExpModal from './AbSplitTestConcludeExpModal';
import {
	AB_SPLIT_TEST_CONSTANTS,
	CONFIG_KEYDATA_CONSTANTS,
	EXPB_DOC_CONSTANTS,
	MULTI_SPLIT_TEST_CONSTANTS
} from '../../../../../../core/constants';
import DropDownSingle from '../../../../../../Components/DropDownSingle';

/// Initializing Variables
const { SITE_STATUS } = EXPB_DOC_CONSTANTS;

const {
	AB_SPLIT_TYPE: { CONTROL },
	CUSTOM_TARGETING: { KEY }
} = AB_SPLIT_TEST_CONSTANTS;

const { CUSTOM_TARGETING_KEYDATA } = CONFIG_KEYDATA_CONSTANTS;

const expStatusArray = Object.keys(SITE_STATUS).map((status) => {
	const disableBool = status === SITE_STATUS.CONCLUDED ? true : false;
	return { value: status, label: status, disable: disableBool };
});

export const MultiSplitTestExperimentCard = ({ experimentData, index }) => {
	const { expbDoc, updateExpbDoc, units, userHasExpbDocEditAccess } = useContext(UnitsContext);
	const { updateModalContent } = useContext(Modalv2Context);
	const multiSplitTestKey = MULTI_SPLIT_TEST_CONSTANTS.IDENTIFIER;

	/**
	 * @name updatePropertyOfExpObj
	 * @description This function is used to update a property of experiment object in expb doc
	 */
	const updatePropertyOfExpObj = (key, newVal) => {
		const clonedExpbDoc = structuredClone(expbDoc);
		const allExperimentsArray = clonedExpbDoc.experiments;
		allExperimentsArray[index][key] = newVal;
		updateExpbDoc(clonedExpbDoc);
	};

	/**
	 * @description Finds the control section unit based on the control video section ID.
	 * @constant {Object} controlSectionUnit - The unit with CONTROL split type.
	 */
	const controlSectionUnit = useMemo(() => {
		const controlSectionRef = Object.keys(units).find((sectionRef) => {
			return (
				units[sectionRef].videoSectionId === experimentData.controlVideoSectionId &&
				units[sectionRef].multiSplitTestData &&
				units[sectionRef].multiSplitTestData.splitType &&
				units[sectionRef].multiSplitTestData.splitType === CONTROL
			);
		});

		if (!controlSectionRef) {
			return null;
		}

		return units[controlSectionRef];
	}, [units, experimentData.controlVideoSectionId]);

	/**
	 * Counts the number of experiment sections for the given control video section ID.
	 * @constant {number} getNumberOfSplit - Number of experiment sections.
	 */
	const getNumberOfSplit = useMemo(() => {
		if (controlSectionUnit) {
			const experimentSections = controlSectionUnit[multiSplitTestKey].experimentalRefs;
			return experimentSections && experimentSections.length;
		}
	}, [units, experimentData.controlVideoSectionId]);

	return (
		<Grid
			container
			spacing={1}
			style={{
				width: '100%',
				margin: '1rem auto'
			}}
		>
			{experimentData && (
				<>
					<Grid item xs={12} style={{ marginBottom: '2rem' }}>
						<Chip label="Multi Split Test" color="primary" />
					</Grid>

					{experimentData.status !== SITE_STATUS.CONCLUDED && (
						<>
							<Grid item xs={3}>
								<InputConfig
									editable={false}
									label="Control Section Name"
									labelStyles={{ width: '90%' }}
									inputStyles={{ width: '90%' }}
									isObjectEditor={false}
									directValue={controlSectionUnit?.name}
								/>
							</Grid>
							<Grid item xs={3}>
								<InputConfig
									editable={false}
									label="Number of Split"
									labelStyles={{ width: '90%' }}
									inputStyles={{ width: '90%' }}
									isObjectEditor={false}
									inputType="number"
									directValue={getNumberOfSplit}
								/>
							</Grid>
							<Grid item xs={3}>
								<InputConfig
									editable={false}
									label="Control KVP"
									isObjectEditor={false}
									directValue={
										controlSectionUnit &&
										controlSectionUnit[`${CUSTOM_TARGETING_KEYDATA}.targeting`][KEY]
									}
								/>
							</Grid>
						</>
					)}

					<Grid item xs={3}>
						<InputConfig
							editable={userHasExpbDocEditAccess && experimentData.status !== SITE_STATUS.CONCLUDED}
							label="Context Url (JIRA)"
							labelStyles={{ width: '90%' }}
							inputStyles={{ width: '90%' }}
							isObjectEditor={false}
							directValue={experimentData.contextUrl}
							updateDirectValue={(newVal) => updatePropertyOfExpObj('contextUrl', newVal)}
						/>
					</Grid>

					<Grid item xs={3}>
						<DropDownSingle
							disabled={
								!userHasExpbDocEditAccess || experimentData.status === SITE_STATUS.CONCLUDED
							}
							isObjectEditor={false}
							directValue={experimentData.status}
							list={expStatusArray}
							label="Status"
							formControlStyles={{ width: '90%' }}
							updateDirectValue={(newVal) => updatePropertyOfExpObj('status', newVal)}
							labelStyles={{ width: '100%' }}
							formControlVariant={'outlined'}
						/>
					</Grid>
					<Grid item xs={3} marginBottom="3rem">
						<InputConfig
							label="Exp Owner Email"
							isObjectEditor={false}
							labelStyles={{ width: '90%' }}
							inputStyles={{ width: '90%' }}
							directValue={experimentData.email}
							editable={false}
							updateDirectValue={(newVal) => updatePropertyOfExpObj('email', newVal)}
						/>
					</Grid>
					<Grid item xs={3} marginBottom="3rem">
						<InputConfig
							editable={userHasExpbDocEditAccess && experimentData.status !== SITE_STATUS.CONCLUDED}
							label="Comment"
							isObjectEditor={false}
							labelStyles={{ width: '90%' }}
							inputStyles={{ width: '90%' }}
							directValue={experimentData.comment}
							updateDirectValue={(newVal) => updatePropertyOfExpObj('comment', newVal)}
							multiline
							rows={4}
						/>
					</Grid>
					{userHasExpbDocEditAccess && experimentData.status !== SITE_STATUS.CONCLUDED && (
						<Grid item xs={12}>
							<Button
								color="primary"
								variant="contained"
								onClick={() => {
									updateModalContent(
										<AbSplitTestConcludeExpModal experimentData={experimentData} index={index} />
									);
								}}
								style={{ float: 'right', marginRight: '2rem' }}
							>
								Conclude Experiment
							</Button>
						</Grid>
					)}
				</>
			)}
		</Grid>
	);
};
